import React, { useEffect, useState } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import axios from "axios";
import { CSVLink } from "react-csv";
import Swal from "sweetalert2";
import { baseurl } from "../../baseurl";

function ReturnReport() {
  const [returns, setReturns] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [openInvoice, setOpenInvoice] = useState(null);

  // 🔁 Fetch Data
  const fetchReturnReport = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${baseurl}return-report`);

      if (res.data.success) {
        setReturns(res.data.data || []);
        setSummary(res.data.summary || {});
      } else {
        Swal.fire("No Data", "No return report found.", "info");
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to fetch return report.", "error");
    } finally {
      setLoading(false);
    }
  };

  // 🔄 Reverse Return Action
  const handleReverseReturn = async (returnId, medicineName) => {
    Swal.fire({
      title: "Are you sure?",
      text: `This will cancel the return for ${medicineName} and restore the quantity back to the invoice!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, reverse it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          // POST request to your endpoint passing the return_id
          const res = await axios.post(`${baseurl}reverse-return/${returnId}`);

          if (res.data.success) {
            Swal.fire("Reversed!", "The return flow has been completely undone.", "success");
            
            // Re-fetch data from backend to automatically update the tables and remove the reversed item
            fetchReturnReport();
          } else {
            Swal.fire("Failed", res.data.message || "Could not reverse return.", "error");
          }
        } catch (err) {
          console.error(err);
          Swal.fire("Error", "Server error while processing reversal.", "error");
        }
      }
    });
  };

  useEffect(() => {
    fetchReturnReport();
  }, []);

  // 📆 Month Helper (YYYY-MM)
  const getMonthKey = (date) => {
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  };

  // 📆 Available Months
  const availableMonths = [
    ...new Set(returns.map((r) => getMonthKey(r.return_date))),
  ];

  // 🔍 Filters
  const filtered = returns.filter((r) => {
    const matchSearch = r.medicine_name
      ?.toLowerCase()
      .includes(search.toLowerCase());

    const matchMonth = selectedMonth
      ? getMonthKey(r.return_date) === selectedMonth
      : true;

    return matchSearch && matchMonth;
  });

  // 📌 Group by Invoice
  const invoices = filtered.reduce((acc, row) => {
    if (!acc[row.invoice_id]) acc[row.invoice_id] = [];
    acc[row.invoice_id].push(row);
    return acc;
  }, {});

  // 💰 Grand Total Refund
  const grandTotalRefund = filtered.reduce(
    (sum, item) => sum + Number(item.refund_amount || 0),
    0
  );

  return (
    <>
      <Topbar />
      <Sidebar />

      <main className="main-wrapper bg-light">
        <div className="container py-0">

          {/* 🔍 Filters */}
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <h5 className="fw-bold mb-0">📋 Return Report</h5>

            <div className="d-flex gap-2 flex-wrap">
              <input
                type="text"
                className="form-control"
                style={{ width: 220 }}
                placeholder="Search medicine..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

              <select
                className="form-select"
                style={{ width: 160 }}
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
              >
                <option value="">All Months</option>
                {availableMonths.map((m) => (
                  <option key={m} value={m}>
                    {new Date(m + "-01").toLocaleString("default", {
                      month: "long",
                      year: "numeric",
                    })}
                  </option>
                ))}
              </select>

              <CSVLink
                data={filtered}
                filename={"return-report.csv"}
                className="btn btn-outline-primary btn-sm"
              >
                Export CSV
              </CSVLink>
            </div>
          </div>

          {/* 📊 TABLE */}
          <div className="card shadow-sm border-0">
            <div className="card-body">
              {loading ? (
                <p className="text-center py-3">Loading returns...</p>
              ) : Object.keys(invoices).length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-striped align-middle">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Invoice ID</th>
                        <th>Customer</th>
                        <th>Phone</th>
                        <th>Total Returns</th>
                        <th>Expand</th>
                      </tr>
                    </thead>

                    <tbody>
                      {Object.keys(invoices).map((inv, index) => {
                        const rows = invoices[inv];
                        const first = rows[0];

                        return (
                          <React.Fragment key={inv}>
                            <tr className="fw-bold">
                              <td>{index + 1}</td>
                              <td>#{inv}</td>
                              <td>{first.customer_name}</td>
                              <td>{first.phone}</td>
                              <td>{rows.length}</td>
                              <td>
                                <button
                                  className="btn btn-sm btn-primary"
                                  onClick={() =>
                                    setOpenInvoice(
                                      openInvoice === inv ? null : inv
                                    )
                                  }
                                >
                                  {openInvoice === inv ? "Hide" : "View"}
                                </button>
                              </td>
                            </tr>

                            {openInvoice === inv && (
                              <tr>
                                <td colSpan="6" className="bg-light">
                                  <table className="table table-bordered mb-0 small">
                                    <thead className="table-dark">
                                      <tr>
                                        <th>Date</th>
                                        <th>Medicine</th>
                                        <th>Batch</th>
                                        <th>Qty Sold</th>
                                        <th>Qty Returned</th>
                                        <th>Refund ₹</th>
                                        <th>Reason</th>
                                        <th className="text-center">Action</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {rows.map((r, i) => (
                                        <tr key={i}>
                                          <td>
                                            {new Date(
                                              r.return_date
                                            ).toLocaleDateString("en-GB")}
                                          </td>
                                          <td>{r.medicine_name}</td>
                                          <td>{r.batch_number || "-"}</td>
                                          <td>{r.qty_sold}</td>
                                          <td className="text-danger fw-bold">
                                            {r.qty_returned}
                                          </td>
                                          <td>₹{r.refund_amount}</td>
                                          <td>{r.reason || "-"}</td>
                                          <td className="text-center">
                                            <button
                                              className="btn btn-xs btn-outline-danger btn-sm"
                                              onClick={() =>
                                                handleReverseReturn(r.return_id, r.medicine_name)
                                              }
                                            >
                                              ↩ Reverse
                                            </button>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                    <tfoot>
                                      <tr>
                                        <td colSpan="5" className="text-end fw-bold">
                                          Total Refund:
                                        </td>
                                        <td className="fw-bold text-success" colSpan="3">
                                          ₹{rows
                                            .reduce(
                                              (sum, item) =>
                                                sum +
                                                Number(item.refund_amount || 0),
                                              0
                                            )
                                            .toFixed(2)}
                                        </td>
                                      </tr>
                                    </tfoot>
                                  </table>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>

                    {/* 🔚 MAIN TABLE FOOTER */}
                    <tfoot>
                      <tr className="fw-bold bg-light">
                        <td colSpan="4" className="text-end">
                          Grand Total Refunded:
                        </td>
                        <td colSpan="2" className="text-success">
                          ₹{grandTotalRefund.toFixed(2)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <p className="text-center py-3">No records found.</p>
              )}
            </div>
          </div>
        </div>
      </main>
    </>
  );
}

export default ReturnReport;